import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@mui/icons-material/Search';
import './index.css';

import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { PsButtonTuner, PsTextTuner } from 'stencilSetup';
import {
  hsaContributionsIntial,
  updateScheduledContribution,
} from 'enrollment/services';
import { EditContributionAccordion } from './EditContributionAccordion';
import { TotalContributionBox } from './TotalContributionBox';
import {
  ScheduledContributionDto,
} from 'enrollment/queries/useV3ContributionAmountQuery';
import { convertDate, getTotalContributionAmount } from "../../../utils/utils";

export const EditContributionsDrawer = () => {
  const initialRefTotalContributionAmount:any = useRef(0);
  const isPayCheckContributionValuesChanged = useRef(false);
  const { t } = useTranslation('pages.financial');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [
    perPayPeriodContributionsUpdated,
    setperPayPeriodContributionsUpdated,
  ] = useState<number>(0);
  const [exceedsTotalContribution, setExceedsTotalContribution] =
    useState(false);
  const dispatch = useDispatch();
  const hsaContribution = useSelector(hsaContributionsIntial);
  const [localScheduledContributions, setLocalScheduledContributions] =
    useState<ScheduledContributionDto[]>([]);
  const [searchPaydayText, setsearchPaydayText] = useState("");

  useEffect(() => {
    if (hsaContribution?.scheduled_contribution) {
      setLocalScheduledContributions(hsaContribution.scheduled_contribution);
    }
  }, [hsaContribution.scheduled_contribution]);


  const getPerPayPeriodBalanceContribution = useCallback(
    (balanceAmount: number) => {
      let unchangedContributions = 0;
      for (let i = 0; i < localScheduledContributions.length; i++) {
        if (!localScheduledContributions[i].isPerPayContributionReduced) {
          unchangedContributions++;
        }
      }
      return balanceAmount / unchangedContributions;
    },
    [localScheduledContributions]
  );

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setperPayPeriodContributionsUpdated(0);
    setLocalScheduledContributions(hsaContribution?.scheduled_contribution);
    isPayCheckContributionValuesChanged.current = false;
  };

  const handleSubmit = () => {
    const finalSchduledContributions = localScheduledContributions.map(({ isPerPayContributionReduced: _, ...item }) => item);
    dispatch(
      updateScheduledContribution({
        scheduled_contribution: finalSchduledContributions,
        disable_hsa_form: true,
      })
    );
    setDrawerOpen(false);
    isPayCheckContributionValuesChanged.current = false;
    setperPayPeriodContributionsUpdated(0);
  };

  const perPayContributionModifiedType = (verifyModifiedContributionType:string, data:any, contributionObj:any) => {
    const changedContributionValue = Number(data.scheduled_subscriber_contribution);
    const changedLumpsumValue = Number(data.scheduled_subscriber_lump_sum_contribution);
    const initialContributionValue = Number(contributionObj.scheduled_subscriber_contribution);
    const initialLumpsumValue = Number(contributionObj.scheduled_subscriber_lump_sum_contribution);
    switch (verifyModifiedContributionType) {
      case 'reduced':
        return ((changedContributionValue < initialContributionValue) || (changedLumpsumValue < initialLumpsumValue)) ? true : false;
      case 'increased':
        return ((changedContributionValue > initialContributionValue) || (changedLumpsumValue > initialLumpsumValue)) ? true : false;
      default:
        return false;
    }
  };

  const handleAccordionChangeValues = (index: number, data: any) => {
    if (hsaContribution.scheduled_contribution) {
      const initialSchduledContributions = hsaContribution.scheduled_contribution;
      const updatedContributions = [...localScheduledContributions];
      const initialSchduledContributionsIndex = initialSchduledContributions[index];
      const isPayCheckContributionReduced:boolean = perPayContributionModifiedType('reduced', data, initialSchduledContributionsIndex);
      const isPayCheckContributionModified:boolean = perPayContributionModifiedType('increased', data, initialSchduledContributionsIndex);

      updatedContributions[index] = {
        ...updatedContributions[index],
        scheduled_subscriber_contribution: data.scheduled_subscriber_contribution,
        scheduled_subscriber_lump_sum_contribution: data.scheduled_subscriber_lump_sum_contribution,
        is_skip_pay_date: data.is_skip_pay_date,
        isPerPayContributionReduced: isPayCheckContributionReduced,
      };
      let ContributionValuesReducedLength = 0;
      for (let i = 0; i < updatedContributions.length; i++) {
        updatedContributions[i].isPerPayContributionReduced && ContributionValuesReducedLength++;
      }
      isPayCheckContributionValuesChanged.current = ContributionValuesReducedLength ? false : (ContributionValuesReducedLength || isPayCheckContributionModified) ? true : false;
      const previousContributionTotal = getTotalContributionAmount(initialSchduledContributions);
      const updatedContributionTotal = getTotalContributionAmount(updatedContributions);
      if (ContributionValuesReducedLength && previousContributionTotal === updatedContributionTotal) {
        isPayCheckContributionValuesChanged.current = true;
      }
      setLocalScheduledContributions(updatedContributions);
      setperPayPeriodContributionsUpdated(ContributionValuesReducedLength);
    }
  };

  const adjustContrubtionAmount = useCallback(
    (adjustContrubutionOption: string, balanceContributionAmount: number) => {
      if (adjustContrubutionOption === 'yes') {
        const adjustedContributionAmount: any = localScheduledContributions.map(
          (item) => {
            if (!item.isPerPayContributionReduced) {
              const scheduledSubscriberContribution =
                Number(item.scheduled_subscriber_contribution) +
                getPerPayPeriodBalanceContribution(
                  balanceContributionAmount
                );
              return {
                ...item,
                scheduled_subscriber_contribution:
                  scheduledSubscriberContribution.toFixed(2),
              };
            } else {
              return item;
            }
          }
        );
        setLocalScheduledContributions(adjustedContributionAmount);
      }
      setperPayPeriodContributionsUpdated(0);
      isPayCheckContributionValuesChanged.current = true;
    },
    [localScheduledContributions, getPerPayPeriodBalanceContribution]
  );

  const handleDrawerOpen = () => {
    if (hsaContribution?.scheduled_contribution) {
      const schduledContributions = hsaContribution?.scheduled_contribution;
      const initialTotalContributionAmount = getTotalContributionAmount(schduledContributions);
      initialRefTotalContributionAmount.current = initialTotalContributionAmount;
    }
    setDrawerOpen(true);
  };
  const isScreenSmall = window.innerWidth < 821;

  const handleSearchPayday = (e:any) => {
    setsearchPaydayText(e.detail.value);
  };

  const scheduledContributionAccordions = () => {
    const filteringPayDays = localScheduledContributions?.filter((itemPay: any) => convertDate(itemPay.paycheck).toLowerCase().includes(searchPaydayText.toLowerCase()));
    if (filteringPayDays.length) {
      return filteringPayDays?.map(
        (payPeriod: ScheduledContributionDto, index: number) => {
          return (
            <div key={index}>
              <EditContributionAccordion
                handleAccordionChangeValues={
                  handleAccordionChangeValues
                }
                key={index}
                payPeriod={payPeriod}
                id={index}
              />
            </div>
          );
        }
      );
    } else {
      return <div className="text-center">
        <div>
          <svg width="113" height="120" viewBox="0 0 113 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M103.195 80.1523L99.2598 84.2981L108.123 93.1609C109.237 94.2766 111.048 94.2766 112.163 93.1609C113.278 92.0466 113.278 90.2352 112.163 89.1209L103.195 80.1523Z" fill="#707178" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M101.173 78.1334L98.3877 75.3477C97.1862 76.8319 95.8319 78.1862 94.3477 79.3877L97.2376 82.2791L101.173 78.1334Z" fill="#707178" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M77.2857 34C90.69 34 101.571 44.8814 101.571 58.2857C101.571 71.69 90.69 82.5714 77.2857 82.5714C63.8814 82.5714 53 71.69 53 58.2857C53 44.8814 63.8814 34 77.2857 34ZM77.2857 36.8571C65.4586 36.8571 55.8571 46.4586 55.8571 58.2857C55.8571 70.1129 65.4586 79.7143 77.2857 79.7143C89.1129 79.7143 98.7143 70.1129 98.7143 58.2857C98.7143 46.4586 89.1129 36.8571 77.2857 36.8571Z" fill="#707178" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M77.2861 44.0019C85.1708 44.0019 91.5717 50.4029 91.5717 58.2875C91.5717 59.0758 92.2121 59.7162 93.0004 59.7162C93.7887 59.7162 94.4291 59.0758 94.4291 58.2875C94.4291 48.8262 86.7474 41.1445 77.2861 41.1445C76.4978 41.1445 75.8574 41.7849 75.8574 42.5732C75.8574 43.3616 76.4978 44.0019 77.2861 44.0019Z" fill="#91929D" />
            <path opacity="0.8" d="M54.6945 0.097702C45.2097 1.09226 34.653 5.11941 26.435 10.8911C21.3515 14.4617 16.9015 18.7661 13.4746 23.4128C8.14755 30.6518 5.01302 39.0322 4.18473 48.3256C3.92487 51.081 4.07104 57.8147 4.42834 60.4071C5.2404 66.2766 6.65337 71.0211 9.07329 75.8961C12.6301 83.1026 17.5349 88.8743 24.6485 94.2547C30.0405 98.3307 38.226 102.407 45.1934 104.494C50.5043 106.075 55.6527 106.972 60.2164 107.086C72.2511 107.428 89.4016 100.124 99.0488 90.5699C100.884 88.7601 103.743 85.1895 104.896 83.2656C109.719 75.3091 110.71 65.2983 107.722 54.9288C105.935 48.7332 101.29 38.804 96.4827 30.8475C92.2438 23.8367 88.0211 18.2769 83.1163 13.2226C75.9378 5.8531 68.8729 1.614 61.5644 0.325958C59.8266 0.0161819 56.5622 -0.0979538 54.6945 0.097702Z" fill="#E6E6E6" />
            <path opacity="0.8" d="M23.107 108.252C22.9933 107.31 22.5602 106.438 21.8511 105.697C21.0174 104.831 20.2865 104.36 17.7259 103.039C15.528 101.913 15.1761 101.691 13.3139 100.321C10.6125 98.3236 9.38904 97.7714 7.85159 97.8418C6.00015 97.9338 4.2624 99.2709 2.43803 102.01C-0.236269 106.038 -0.712662 109.871 1.01968 113.395C1.63141 114.629 2.41096 115.674 3.52616 116.757C5.44797 118.614 7.16948 119.474 9.75175 119.87C10.6829 120.016 12.74 120.048 13.5575 119.929C14.7052 119.761 16.1127 119.334 17.0384 118.868C18.2835 118.24 19.5665 117.119 20.5626 115.799C21.7969 114.158 22.8363 111.69 23.0908 109.795C23.172 109.167 23.1774 108.82 23.107 108.252Z" fill="#E6E6E6" />
          </svg>

        </div>
        <div className='semiBold noResultsFoundTitle mt-2'>{t('editContribution.searchPayDayNoResultsTitle')}</div>
        <div className='noResultsFoundDescription mt-1 pb-5'>{t('editContribution.searchPayDayNoResultsDescription')}</div>
        </div>;
    }
};

  return (
    <div className="pt-4 border-top">
      <h3>{t('editContribution.desktop_title')}</h3>
      <p className="helper-text edit-payperiod-description w-100">
        {t('editContribution.description')}
      </p>
      <div>
        <div>
          <div className="d-flex flex-column">
            <div className="position-relative">
              <div className="pr-3_5">
                <PsButtonTuner
                  buttonId="edit-contributions"
                  data-testid="edit-contributions"
                  level="secondary"
                  label={t('editContribution.drawerButton')}
                  extraClass="mb-3_5"
                  onClick={handleDrawerOpen}
                />
              </div>
              <OrbitDrawer
                drawerId="edit_contribution_drawer"
                heading={t(isScreenSmall ? 'editContribution.mobile_title' : 'editContribution.desktop_title')}
                isOpen={drawerOpen}
                handleSubmit={handleSubmit}
                handleClose={handleDrawerClose}
                footer={true}
                saveButton="Submit"
                saveButtonDisabled={!isPayCheckContributionValuesChanged.current || exceedsTotalContribution}
                dialogLarge={true}
              >
                <div>
                  <p className="my-5">{t('editContribution.description')}</p>
                  <div className='view-edit-drawer-accordions mt-5'>
                  <div className="view-edit-drawer-filter px-4 py-3">
                    <div className="w-25 position-relative" data-testid="view-edit-drawer-filter">
                    <div className="position-absolute searchPaydayIcon"><SearchIcon/></div>
                    <PsTextTuner
                      data-testid="searchPayDays"
                      inputId="payDaySearchId"
                      name="Search by Payday"
                      label=" "
                      type="text"
                      placeholder="Search by Payday"
                      value={searchPaydayText}
                      onPsChange={handleSearchPayday}
                    />
                    </div>
                  </div>
                  <div
                    className="accordion accordion-flush p-4"
                    id="paycheck-accordions"
                    data-collapse-all
                  >
                    {scheduledContributionAccordions()}
                  </div>
                  </div>
                  <TotalContributionBox
                    localScheduledContributions={localScheduledContributions}
                    adjustContrubtionAmount={adjustContrubtionAmount}
                    perPayPeriodContributionsUpdated={
                      perPayPeriodContributionsUpdated
                    }
                    exceedsTotalContribution={exceedsTotalContribution}
                    setExceedsTotalContribution={setExceedsTotalContribution}
                    initialTotalContributionAmount={initialRefTotalContributionAmount.current}
                  />
                </div>
              </OrbitDrawer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
